import { useMutation, useQuery } from '@tanstack/react-query'
import { BackupsFilterParams, CreateBackupScheduleParams, UpdateBackupScheduleParams } from '@/types/backup'
import { useBackupSchedulesStore } from '@/store'
import {
  fetchBackups,
  fetchBackup,
  createBackupSchedule,
  updateBackupSchedule,
  deleteBackupSchedule,
  bulkDeleteBackupSchedules,
  runBackupSchedule
} from '@/lib/api'
import { useBulkDeleteSuccessHandler } from './use-bulk-delete'

export const useBackupSchedules = (params: BackupsFilterParams, options?: { enabled?: boolean }) =>
  useQuery({
    queryKey: ['backup-schedules', params],
    queryFn: () => fetchBackups(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: options?.enabled ?? true
  })

export const useBackupSchedule = (id: string) =>
  useQuery({
    queryKey: ['backup-schedule', id],
    queryFn: () => fetchBackup(id),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    enabled: !!id
  })

export const useCreateBackupSchedule = () =>
  useMutation({
    mutationFn: (params: CreateBackupScheduleParams) => createBackupSchedule(params),
    onSuccess: () => {}
  })

export const useUpdateBackupSchedule = () =>
  useMutation({
    mutationFn: ({ id, params }: { id: string; params: UpdateBackupScheduleParams }) =>
      updateBackupSchedule(id, params),
    onSuccess: () => {}
  })

export const useDeleteBackupSchedule = () =>
  useMutation({
    mutationFn: (id: string) => deleteBackupSchedule(id),
    onSuccess: () => {}
  })

export const useBulkDeleteBackupSchedules = () => {
  const deselectAllBackups = useBackupSchedulesStore(state => state.deselectAllBackups)
  const handleBulkDeleteSuccess = useBulkDeleteSuccessHandler()

  return useMutation({
    mutationFn: (ids: string[]) => bulkDeleteBackupSchedules({ ids }),
    onSuccess: response => {
      deselectAllBackups()
      handleBulkDeleteSuccess(response)
    }
  })
}

export const useRunBackupSchedule = () =>
  useMutation({
    mutationFn: (id: string) => runBackupSchedule(id),
    onSuccess: () => {}
  })
